import {
	createContext,
	Dispatch,
	ReactElement,
	SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { FORM_STATES, useFormState } from '@/hooks/forms';

import { useAuthPrompt } from './auth/AuthPromptProvider';

type FormAction = { type: string };

interface FormStateContextState {
	formState: string;
	formDispatch: Dispatch<FormAction>;
	formSuccess: string | null;
	setFormSuccess: Dispatch<SetStateAction<string | null>>;
	formError: string | null;
	setFormError: Dispatch<SetStateAction<string | null>>;
}

export const FormStateContext = createContext<FormStateContextState>({
	formState: FORM_STATES.idle,
	formDispatch: () => {},
	formSuccess: null,
	setFormSuccess: () => {},
	formError: null,
	setFormError: () => {},
});

export const FormStateContextProvider = ({ children }: { children?: ReactElement }) => {
	const { authPrompt } = useAuthPrompt();

	const [formState, formDispatch] = useFormState();
	const [formSuccess, setFormSuccess] = useState<string | null>(null);
	const [formError, setFormError] = useState<string | null>(null);

	useEffect(() => {
		// TODO: refactor this code to follow best practices
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setFormError(null);
		return () => {
			setFormError(null);
		};
	}, [authPrompt]);

	const value = useMemo(
		() => ({
			formState,
			formDispatch,
			formSuccess,
			setFormSuccess,
			formError,
			setFormError,
		}),
		[formDispatch, formError, formState, formSuccess],
	);

	return <FormStateContext.Provider value={value}>{children}</FormStateContext.Provider>;
};

export const useFormStateContext = (): FormStateContextState => {
	const value = useContext(FormStateContext);
	return value;
};
